import React, { useState, useEffect, useCallback } from "react";
import { API, useLanguage } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  Briefcase, 
  Clock, 
  Settings, 
  Plus, 
  Trash2, 
  Save,
  QrCode,
  Copy,
  ExternalLink,
  Users,
  MessageCircle,
  CheckCircle,
  AlertCircle,
  Loader2,
  X,
  Ban,
  Power,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const TenantManageDialog = ({ tenant, open, onOpenChange, onTenantUpdate }) => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [settings, setSettings] = useState({});
  const [qrData, setQrData] = useState(null);
  const [activeTab, setActiveTab] = useState("services");
  const [suspended, setSuspended] = useState(Boolean(tenant?.suspended));
  const [suspendBusy, setSuspendBusy] = useState(false);

  useEffect(() => {
    setSuspended(Boolean(tenant?.suspended));
  }, [tenant?.suspended, tenant?.tenant_id]);

  const handleToggleSuspend = async () => {
    if (suspendBusy) return;
    const action = suspended ? "unsuspend" : "suspend";
    const confirmMsg = suspended
      ? (language === "it"
          ? `Riattivare l'account di "${tenant.name}"? L'utente potrà rifare login.`
          : `Re-activate "${tenant.name}"? The user will be able to log in again.`)
      : (language === "it"
          ? `Sospendere l'account di "${tenant.name}"?\n\nL'utente non potrà più fare login finché non riattivi l'account o non rinnovi l'abbonamento. Tutti i dati restano salvati.`
          : `Suspend "${tenant.name}"?\n\nThe user will not be able to log in until you re-activate the account or they renew the subscription. All data is preserved.`);
    if (!window.confirm(confirmMsg)) return;
    setSuspendBusy(true);
    try {
      await axios.post(
        `${API}/superadmin/tenants/${tenant.tenant_id}/${action}`,
        action === "suspend" ? { reason: "trial_expired" } : {},
        { withCredentials: true }
      );
      const newState = !suspended;
      setSuspended(newState);
      onTenantUpdate?.({ ...tenant, suspended: newState, subscription_status: newState ? "suspended" : "active" });
      toast.success(
        newState
          ? (language === "it" ? "Account sospeso" : "Account suspended")
          : (language === "it" ? "Account riattivato" : "Account re-activated")
      );
    } catch (err) {
      toast.error(language === "it" ? "Operazione fallita" : "Operation failed");
    } finally {
      setSuspendBusy(false);
    }
  };
  
  // WhatsApp configuration state
  const [whatsappConfig, setWhatsappConfig] = useState(null);
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [whatsappForm, setWhatsappForm] = useState({
    phone_number_id: "",
    access_token: "",
    waba_id: "",
    display_phone: ""
  });
  const [whatsappSettings, setWhatsappSettings] = useState({
    enabled: true,
    send_booking_confirmation: true,
    send_reminder_12h: true,
    send_reminder_2h: true
  });
  
  // New service form
  const [newService, setNewService] = useState({
    name: "",
    duration: 30,
    price: 0,
    category: "Generale",
    description: "",
    is_active: true
  });
  
  // New staff form
  const [newStaff, setNewStaff] = useState({
    name: "",
    email: "",
    phone: "",
    is_active: true
  });

  const durations = [10, 15, 20, 30, 45, 60, 75, 90, 105, 120, 150, 180, 210, 240, 270, 300];

  const fetchData = useCallback(async () => {
    if (!tenant?.tenant_id || !open) return;
    
    setLoading(true);
    try {
      const [servicesRes, staffRes, settingsRes, qrRes] = await Promise.all([
        axios.get(`${API}/superadmin/tenants/${tenant.tenant_id}/services`, { withCredentials: true }),
        axios.get(`${API}/superadmin/tenants/${tenant.tenant_id}/staff`, { withCredentials: true }),
        axios.get(`${API}/superadmin/tenants/${tenant.tenant_id}/settings`, { withCredentials: true }),
        axios.get(`${API}/superadmin/tenants/${tenant.tenant_id}/qrcode`, { withCredentials: true })
      ]);
      
      setServices(servicesRes.data);
      setStaff(staffRes.data);
      setSettings(settingsRes.data);
      setQrData(qrRes.data);
    } catch (error) {
      console.error("Error fetching tenant data:", error);
      toast.error(language === "it" ? "Errore nel caricamento dati" : "Error loading data");
    } finally {
      setLoading(false);
    }
  }, [tenant?.tenant_id, open, language]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ========== SERVICES ==========
  const handleAddService = async () => {
    if (!newService.name) {
      toast.error(language === "it" ? "Inserisci il nome del servizio" : "Enter service name");
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/superadmin/tenants/${tenant.tenant_id}/services`,
        newService,
        { withCredentials: true }
      );
      
      setServices([...services, response.data]);
      setNewService({ name: "", duration: 30, price: 0, category: "Generale", description: "", is_active: true });
      toast.success(language === "it" ? "Servizio aggiunto!" : "Service added!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    }
  };

  const handleDeleteService = async (serviceId) => {
    try {
      await axios.delete(
        `${API}/superadmin/tenants/${tenant.tenant_id}/services/${serviceId}`,
        { withCredentials: true }
      );
      
      setServices(services.filter(s => s.service_id !== serviceId));
      toast.success(language === "it" ? "Servizio eliminato" : "Service deleted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    }
  };

  const handleUpdateService = async (serviceId, updates) => {
    try {
      const response = await axios.patch(
        `${API}/superadmin/tenants/${tenant.tenant_id}/services/${serviceId}`,
        updates,
        { withCredentials: true }
      );
      
      setServices(services.map(s => s.service_id === serviceId ? response.data : s));
      toast.success(language === "it" ? "Servizio aggiornato" : "Service updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    }
  };

  // ========== STAFF ==========
  const handleAddStaff = async () => {
    if (!newStaff.name) {
      toast.error(language === "it" ? "Inserisci il nome" : "Enter name");
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/superadmin/tenants/${tenant.tenant_id}/staff`,
        newStaff,
        { withCredentials: true }
      );
      
      setStaff([...staff, response.data]);
      setNewStaff({ name: "", email: "", phone: "", is_active: true });
      toast.success(language === "it" ? "Staff aggiunto!" : "Staff added!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    }
  };

  const handleDeleteStaff = async (staffId) => {
    try {
      await axios.delete(
        `${API}/superadmin/tenants/${tenant.tenant_id}/staff/${staffId}`,
        { withCredentials: true }
      );
      
      setStaff(staff.filter(s => s.staff_id !== staffId));
      toast.success(language === "it" ? "Staff eliminato" : "Staff deleted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    }
  };

  // ========== SETTINGS ==========
  const handleSaveSettings = async () => {
    try {
      const response = await axios.patch(
        `${API}/superadmin/tenants/${tenant.tenant_id}/settings`,
        {
          name: settings.name,
          phone: settings.phone,
          address: settings.address,
          settings: settings.settings
        },
        { withCredentials: true }
      );
      
      setSettings(response.data);
      toast.success(language === "it" ? "Impostazioni salvate!" : "Settings saved!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    }
  };

  // ========== WHATSAPP ==========
  const fetchWhatsAppConfig = useCallback(async () => {
    if (!tenant?.tenant_id || !open) return;
    
    setWhatsappLoading(true);
    try {
      const response = await axios.get(
        `${API}/whatsapp/admin/config/${tenant.tenant_id}`,
        { withCredentials: true }
      );
      setWhatsappConfig(response.data);
      if (response.data.configured) {
        setWhatsappForm({
          phone_number_id: response.data.phone_number_id || "",
          access_token: "",  // Never show the token
          waba_id: response.data.waba_id || "",
          display_phone: response.data.display_phone || ""
        });
        setWhatsappSettings(response.data.settings || {
          enabled: true,
          send_booking_confirmation: true,
          send_reminder_12h: true,
          send_reminder_2h: true
        });
      }
    } catch (error) {
      console.error("Error fetching WhatsApp config:", error);
    } finally {
      setWhatsappLoading(false);
    }
  }, [tenant?.tenant_id, open]);

  useEffect(() => {
    if (activeTab === "whatsapp" && tenant?.tenant_id) {
      fetchWhatsAppConfig();
    }
  }, [activeTab, tenant?.tenant_id, fetchWhatsAppConfig]);

  const handleSaveWhatsApp = async () => {
    if (!whatsappForm.phone_number_id || !whatsappForm.access_token) {
      toast.error(language === "it" ? "Phone Number ID e Access Token sono obbligatori" : "Phone Number ID and Access Token are required");
      return;
    }

    setWhatsappLoading(true);
    try {
      await axios.post(
        `${API}/whatsapp/admin/config/${tenant.tenant_id}`,
        whatsappForm,
        { withCredentials: true }
      );
      toast.success(language === "it" ? "WhatsApp configurato!" : "WhatsApp configured!");
      fetchWhatsAppConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    } finally {
      setWhatsappLoading(false);
    }
  };

  const handleRemoveWhatsApp = async () => {
    if (!window.confirm(language === "it" 
      ? "Rimuovere la configurazione WhatsApp per questa attività?" 
      : "Remove WhatsApp configuration for this business?"
    )) return;

    setWhatsappLoading(true);
    try {
      await axios.delete(
        `${API}/whatsapp/admin/config/${tenant.tenant_id}`,
        { withCredentials: true }
      );
      toast.success(language === "it" ? "Configurazione rimossa" : "Configuration removed");
      setWhatsappConfig({ configured: false });
      setWhatsappForm({
        phone_number_id: "",
        access_token: "",
        waba_id: "",
        display_phone: ""
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    } finally {
      setWhatsappLoading(false);
    }
  };

  const handleUpdateWhatsAppSettings = async (key, value) => {
    const newSettings = { ...whatsappSettings, [key]: value };
    setWhatsappSettings(newSettings);
    
    try {
      await axios.patch(
        `${API}/whatsapp/admin/config/${tenant.tenant_id}/settings`,
        newSettings,
        { withCredentials: true }
      );
    } catch (error) {
      toast.error("Error updating settings");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(language === "it" ? "Copiato!" : "Copied!");
  };

  if (!tenant) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <Settings className="w-5 h-5" />
            <span>{language === "it" ? "Gestisci" : "Manage"}: {tenant.name}</span>
            {suspended && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300 border border-rose-300/40"
                data-testid="tenant-suspended-badge"
              >
                <Ban className="w-3 h-3" />
                {language === "it" ? "Sospeso" : "Suspended"}
              </span>
            )}
          </DialogTitle>
          <div className="flex flex-wrap gap-2 mt-2">
            <Button
              variant={suspended ? "default" : "destructive"}
              size="sm"
              onClick={handleToggleSuspend}
              disabled={suspendBusy}
              className={suspended ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}
              data-testid={suspended ? "btn-unsuspend-tenant" : "btn-suspend-tenant"}
            >
              {suspendBusy ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : suspended ? (
                <Power className="w-4 h-4 mr-2" />
              ) : (
                <Ban className="w-4 h-4 mr-2" />
              )}
              {suspended
                ? (language === "it" ? "Riattiva account" : "Re-activate account")
                : (language === "it" ? "Sospendi account" : "Suspend account")}
            </Button>
            <p className="text-[11px] text-muted-foreground flex items-center">
              {suspended
                ? (language === "it"
                    ? "L'utente non può fare login. Tutti i dati sono preservati."
                    : "User cannot log in. All data is preserved.")
                : (language === "it"
                    ? "Sospendi a fine trial: blocca l'accesso senza eliminare i dati."
                    : "Suspend at end of trial: blocks login without deleting data.")}
            </p>
          </div>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="services" className="flex items-center gap-1">
                <Briefcase className="w-4 h-4" />
                <span className="hidden sm:inline">{language === "it" ? "Servizi" : "Services"}</span>
              </TabsTrigger>
              <TabsTrigger value="staff" className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                <span className="hidden sm:inline">{language === "it" ? "Team" : "Team"}</span>
              </TabsTrigger>
              <TabsTrigger value="hours" className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                <span className="hidden sm:inline">{language === "it" ? "Orari" : "Hours"}</span>
              </TabsTrigger>
              <TabsTrigger value="whatsapp" className="flex items-center gap-1">
                <MessageCircle className="w-4 h-4" />
                <span className="hidden sm:inline">WhatsApp</span>
              </TabsTrigger>
              <TabsTrigger value="booking" className="flex items-center gap-1">
                <QrCode className="w-4 h-4" />
                <span className="hidden sm:inline">{language === "it" ? "Link" : "Link"}</span>
              </TabsTrigger>
            </TabsList>

            {/* ========== SERVICES TAB ========== */}
            <TabsContent value="services" className="space-y-4 mt-4">
              {/* Add New Service */}
              <div className="p-4 rounded-lg border bg-muted/30">
                <h3 className="font-medium mb-3">{language === "it" ? "Aggiungi Servizio" : "Add Service"}</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Input
                    placeholder={language === "it" ? "Nome servizio" : "Service name"}
                    value={newService.name}
                    onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                  />
                  <Select
                    value={newService.duration.toString()}
                    onValueChange={(v) => setNewService({ ...newService, duration: parseInt(v) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {durations.map(d => (
                        <SelectItem key={d} value={d.toString()}>{d} min</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="text"
                    inputMode="decimal"
                    placeholder="€ 0,00"
                    value={newService.price.toString().replace('.', ',')}
                    onChange={(e) => {
                      const value = e.target.value.replace(',', '.');
                      setNewService({ ...newService, price: parseFloat(value) || 0 });
                    }}
                  />
                  <Button onClick={handleAddService}>
                    <Plus className="w-4 h-4 mr-1" />
                    {language === "it" ? "Aggiungi" : "Add"}
                  </Button>
                </div>
              </div>

              {/* Services List */}
              <div className="space-y-2">
                {services.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    {language === "it" ? "Nessun servizio" : "No services"}
                  </p>
                ) : (
                  services.map((service) => (
                    <div 
                      key={service.service_id}
                      className="flex items-center justify-between p-3 rounded-lg border bg-card"
                    >
                      <div className="flex-1">
                        <p className="font-medium">{service.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {service.duration} min • €{service.price?.toFixed(2).replace('.', ',')}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleUpdateService(service.service_id, { is_active: !service.is_active })}
                          className={service.is_active ? "text-emerald-600" : "text-muted-foreground"}
                        >
                          {service.is_active ? "✓" : "○"}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteService(service.service_id)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </TabsContent>

            {/* ========== STAFF TAB ========== */}
            <TabsContent value="staff" className="space-y-4 mt-4">
              {/* Add New Staff */}
              <div className="p-4 rounded-lg border bg-muted/30">
                <h3 className="font-medium mb-3">{language === "it" ? "Aggiungi Staff" : "Add Staff"}</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Input
                    placeholder={language === "it" ? "Nome" : "Name"}
                    value={newStaff.name}
                    onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })}
                  />
                  <Input
                    placeholder="Email"
                    value={newStaff.email}
                    onChange={(e) => setNewStaff({ ...newStaff, email: e.target.value })}
                  />
                  <Input
                    placeholder={language === "it" ? "Telefono" : "Phone"}
                    value={newStaff.phone}
                    onChange={(e) => setNewStaff({ ...newStaff, phone: e.target.value })}
                  />
                  <Button onClick={handleAddStaff}>
                    <Plus className="w-4 h-4 mr-1" />
                    {language === "it" ? "Aggiungi" : "Add"}
                  </Button>
                </div>
              </div>

              {/* Staff List */}
              <div className="space-y-2">
                {staff.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    {language === "it" ? "Nessun membro del team" : "No team members"}
                  </p>
                ) : (
                  staff.map((member) => (
                    <div 
                      key={member.staff_id}
                      className="flex items-center justify-between p-3 rounded-lg border bg-card"
                    >
                      <div className="flex-1">
                        <p className="font-medium">{member.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {member.email} • {member.phone}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteStaff(member.staff_id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </TabsContent>

            {/* ========== HOURS TAB ========== */}
            <TabsContent value="hours" className="space-y-4 mt-4">
              <div className="p-4 rounded-lg border">
                <h3 className="font-medium mb-4">{language === "it" ? "Orari di Apertura" : "Opening Hours"}</h3>
                
                <div className="space-y-3">
                  {["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].map((day) => {
                    const dayNames = {
                      monday: language === "it" ? "Lunedì" : "Monday",
                      tuesday: language === "it" ? "Martedì" : "Tuesday",
                      wednesday: language === "it" ? "Mercoledì" : "Wednesday",
                      thursday: language === "it" ? "Giovedì" : "Thursday",
                      friday: language === "it" ? "Venerdì" : "Friday",
                      saturday: language === "it" ? "Sabato" : "Saturday",
                      sunday: language === "it" ? "Domenica" : "Sunday"
                    };
                    
                    const hours = settings.settings?.opening_hours?.[day] || { open: "09:00", close: "18:00", closed: false };
                    
                    return (
                      <div key={day} className="flex items-center gap-4">
                        <span className="w-24 font-medium">{dayNames[day]}</span>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={!hours.closed}
                            onChange={(e) => {
                              const newHours = { ...settings.settings?.opening_hours, [day]: { ...hours, closed: !e.target.checked } };
                              setSettings({ ...settings, settings: { ...settings.settings, opening_hours: newHours } });
                            }}
                            className="w-4 h-4"
                          />
                          {language === "it" ? "Aperto" : "Open"}
                        </label>
                        {!hours.closed && (
                          <>
                            <Input
                              type="time"
                              value={hours.open || "09:00"}
                              onChange={(e) => {
                                const newHours = { ...settings.settings?.opening_hours, [day]: { ...hours, open: e.target.value } };
                                setSettings({ ...settings, settings: { ...settings.settings, opening_hours: newHours } });
                              }}
                              className="w-28"
                            />
                            <span>-</span>
                            <Input
                              type="time"
                              value={hours.close || "18:00"}
                              onChange={(e) => {
                                const newHours = { ...settings.settings?.opening_hours, [day]: { ...hours, close: e.target.value } };
                                setSettings({ ...settings, settings: { ...settings.settings, opening_hours: newHours } });
                              }}
                              className="w-28"
                            />
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                <Button onClick={handleSaveSettings} className="mt-4">
                  <Save className="w-4 h-4 mr-2" />
                  {language === "it" ? "Salva Orari" : "Save Hours"}
                </Button>
              </div>
            </TabsContent>

            {/* ========== WHATSAPP TAB ========== */}
            <TabsContent value="whatsapp" className="space-y-4 mt-4">
              {whatsappLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-green-500" />
                </div>
              ) : whatsappConfig?.configured ? (
                <div className="space-y-4">
                  {/* Connected Status */}
                  <div className="flex items-center gap-3 p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl">
                    <CheckCircle className="w-6 h-6 text-emerald-500" />
                    <div className="flex-1">
                      <p className="font-medium text-emerald-700 dark:text-emerald-300">
                        WhatsApp Business {language === "it" ? "Configurato" : "Configured"}
                      </p>
                      {whatsappConfig.display_phone && (
                        <p className="text-sm text-emerald-600 dark:text-emerald-400">
                          {whatsappConfig.display_phone}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Notification Settings */}
                  <div className="p-4 rounded-lg border space-y-3">
                    <h4 className="font-medium">{language === "it" ? "Impostazioni Notifiche" : "Notification Settings"}</h4>
                    
                    <label className="flex items-center justify-between py-2">
                      <span className="text-sm">{language === "it" ? "Notifiche abilitate" : "Notifications enabled"}</span>
                      <Switch
                        checked={whatsappSettings.enabled}
                        onCheckedChange={(v) => handleUpdateWhatsAppSettings("enabled", v)}
                      />
                    </label>
                    
                    <label className="flex items-center justify-between py-2">
                      <span className="text-sm">{language === "it" ? "Conferma prenotazione" : "Booking confirmation"}</span>
                      <Switch
                        checked={whatsappSettings.send_booking_confirmation}
                        onCheckedChange={(v) => handleUpdateWhatsAppSettings("send_booking_confirmation", v)}
                      />
                    </label>
                    
                    <label className="flex items-center justify-between py-2">
                      <span className="text-sm">{language === "it" ? "Reminder 12h prima" : "Reminder 12h before"}</span>
                      <Switch
                        checked={whatsappSettings.send_reminder_12h}
                        onCheckedChange={(v) => handleUpdateWhatsAppSettings("send_reminder_12h", v)}
                      />
                    </label>
                    
                    <label className="flex items-center justify-between py-2">
                      <span className="text-sm">{language === "it" ? "Reminder 2h prima" : "Reminder 2h before"}</span>
                      <Switch
                        checked={whatsappSettings.send_reminder_2h}
                        onCheckedChange={(v) => handleUpdateWhatsAppSettings("send_reminder_2h", v)}
                      />
                    </label>
                  </div>

                  {/* Update Credentials */}
                  <div className="p-4 rounded-lg border bg-muted/30 space-y-3">
                    <h4 className="font-medium text-sm">{language === "it" ? "Aggiorna Credenziali" : "Update Credentials"}</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-muted-foreground mb-1">Phone Number ID</label>
                        <Input
                          value={whatsappForm.phone_number_id}
                          onChange={(e) => setWhatsappForm({ ...whatsappForm, phone_number_id: e.target.value })}
                          placeholder="123456789012345"
                          className="text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-muted-foreground mb-1">{language === "it" ? "Numero Visualizzato" : "Display Phone"}</label>
                        <Input
                          value={whatsappForm.display_phone}
                          onChange={(e) => setWhatsappForm({ ...whatsappForm, display_phone: e.target.value })}
                          placeholder="+39 320 1234567"
                          className="text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">Access Token ({language === "it" ? "lascia vuoto per mantenere" : "leave empty to keep"})</label>
                      <Input
                        type="password"
                        value={whatsappForm.access_token}
                        onChange={(e) => setWhatsappForm({ ...whatsappForm, access_token: e.target.value })}
                        placeholder="••••••••••••••••"
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">WABA ID ({language === "it" ? "opzionale" : "optional"})</label>
                      <Input
                        value={whatsappForm.waba_id}
                        onChange={(e) => setWhatsappForm({ ...whatsappForm, waba_id: e.target.value })}
                        placeholder="WhatsApp Business Account ID"
                        className="text-sm"
                      />
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button onClick={handleSaveWhatsApp} disabled={whatsappLoading}>
                      <Save className="w-4 h-4 mr-2" />
                      {language === "it" ? "Aggiorna" : "Update"}
                    </Button>
                    <Button variant="destructive" onClick={handleRemoveWhatsApp} disabled={whatsappLoading}>
                      <X className="w-4 h-4 mr-2" />
                      {language === "it" ? "Rimuovi" : "Remove"}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Not Configured */}
                  <div className="flex items-center gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl">
                    <AlertCircle className="w-6 h-6 text-amber-500" />
                    <div>
                      <p className="font-medium text-amber-700 dark:text-amber-300">
                        WhatsApp {language === "it" ? "Non Configurato" : "Not Configured"}
                      </p>
                      <p className="text-sm text-amber-600 dark:text-amber-400">
                        {language === "it" 
                          ? "Inserisci le credenziali WhatsApp Business API per attivare i reminder automatici."
                          : "Enter WhatsApp Business API credentials to enable automatic reminders."
                        }
                      </p>
                    </div>
                  </div>

                  {/* Configuration Form */}
                  <div className="p-4 rounded-lg border space-y-4">
                    <h4 className="font-medium">{language === "it" ? "Configura WhatsApp Business API" : "Configure WhatsApp Business API"}</h4>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium mb-1">Phone Number ID *</label>
                        <Input
                          value={whatsappForm.phone_number_id}
                          onChange={(e) => setWhatsappForm({ ...whatsappForm, phone_number_id: e.target.value })}
                          placeholder="123456789012345"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          {language === "it" ? "Da Meta Business Suite" : "From Meta Business Suite"}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">{language === "it" ? "Numero Visualizzato" : "Display Phone"}</label>
                        <Input
                          value={whatsappForm.display_phone}
                          onChange={(e) => setWhatsappForm({ ...whatsappForm, display_phone: e.target.value })}
                          placeholder="+39 320 1234567"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Access Token *</label>
                      <Input
                        type="password"
                        value={whatsappForm.access_token}
                        onChange={(e) => setWhatsappForm({ ...whatsappForm, access_token: e.target.value })}
                        placeholder="EAAxxxxxxxx..."
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {language === "it" 
                          ? "Token permanente da Meta Business Suite > WhatsApp > Configurazione API"
                          : "Permanent token from Meta Business Suite > WhatsApp > API Configuration"
                        }
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">WABA ID ({language === "it" ? "opzionale" : "optional"})</label>
                      <Input
                        value={whatsappForm.waba_id}
                        onChange={(e) => setWhatsappForm({ ...whatsappForm, waba_id: e.target.value })}
                        placeholder="WhatsApp Business Account ID"
                      />
                    </div>

                    <Button 
                      onClick={handleSaveWhatsApp} 
                      disabled={whatsappLoading || !whatsappForm.phone_number_id || !whatsappForm.access_token}
                      className="w-full bg-green-500 hover:bg-green-600"
                    >
                      {whatsappLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <MessageCircle className="w-4 h-4 mr-2" />
                      )}
                      {language === "it" ? "Configura WhatsApp" : "Configure WhatsApp"}
                    </Button>
                  </div>

                  {/* Help Text */}
                  <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800 text-sm text-muted-foreground">
                    <p className="font-medium mb-2">{language === "it" ? "Come ottenere le credenziali:" : "How to get credentials:"}</p>
                    <ol className="list-decimal list-inside space-y-1 text-xs">
                      <li>{language === "it" ? "Vai su business.facebook.com" : "Go to business.facebook.com"}</li>
                      <li>{language === "it" ? "Seleziona l'account WhatsApp Business del cliente" : "Select the client's WhatsApp Business account"}</li>
                      <li>{language === "it" ? "Vai su WhatsApp > Configurazione API" : "Go to WhatsApp > API Configuration"}</li>
                      <li>{language === "it" ? "Copia Phone Number ID e genera un token permanente" : "Copy Phone Number ID and generate a permanent token"}</li>
                    </ol>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* ========== BOOKING TAB ========== */}
            <TabsContent value="booking" className="space-y-4 mt-4">
              <div className="p-6 rounded-lg border text-center">
                <h3 className="font-medium mb-4">{language === "it" ? "Pagina Prenotazione" : "Booking Page"}</h3>
                
                {qrData && (
                  <>
                    <div className="flex justify-center mb-4">
                      <img 
                        src={qrData.qr_code} 
                        alt="QR Code" 
                        className="w-48 h-48 rounded-lg border"
                      />
                    </div>
                    
                    <div className="flex items-center justify-center gap-2 mb-4">
                      <Input 
                        value={qrData.booking_url} 
                        readOnly 
                        className="max-w-md text-center"
                      />
                      <Button variant="outline" size="icon" onClick={() => copyToClipboard(qrData.booking_url)}>
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="icon" onClick={() => window.open(qrData.booking_url, '_blank')}>
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    <p className="text-sm text-muted-foreground">
                      {language === "it" 
                        ? "Condividi questo link o QR code con i tuoi clienti."
                        : "Share this link or QR code with your clients."
                      }
                    </p>
                  </>
                )}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TenantManageDialog;
